import { z } from "zod";
/**
 * Place taxonomy schemas — the shared contract for the Phase 2 field-registry
 * surface (apps/api/src/routes/places/placeTaxonomy.ts):
 *   * place_detail_definitions CRUD (the profile field registry)
 *   * place_classification_detail_requirements bulk replace (the occupancy →
 *     field-set junction, saved atomically per classification via the
 *     set_classification_detail_requirements RPC)
 * See docs/places/phase-2.md (B1) and docs/BACKEND_API.md.
 */
export const requirementLevelSchema = z.enum([
    "required_for_espm_score",
    "recommended",
    "optional",
]);
/** Writes target the caller's org rows by default; super admins may write the
 * global (organization_id NULL) rows — RLS enforces, the schema just routes. */
export const placeTaxonomyScopeSchema = z.enum(["org", "global"]);
const detailCategorySchema = z.enum([
    "core",
    "use",
    "capacity",
    "access",
    "tech",
    "transport",
    "bio",
]);
const dataTypeSchema = z.enum(["numeric", "text", "boolean", "date", "enum"]);
const premisesLevelsSchema = z
    .array(z.enum(["site", "building", "section"]))
    .nonempty();
/** Namespaced snake_case dotted key, e.g. `use.seating_capacity`. */
const definitionKeySchema = z
    .string()
    .min(3)
    .max(200)
    .regex(/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/, "expected a dotted snake_case key like 'use.seating_capacity'");
const definitionCore = {
    name: z.string().min(1).max(200),
    description: z.string().max(2000).nullable().optional(),
    unit: z.string().max(100).nullable().optional(),
    /** Allowed options for data_type = 'enum' (array of strings). */
    enum_options: z.array(z.string().min(1).max(200)).min(1).nullable().optional(),
    min_value: z.number().finite().nullable().optional(),
    max_value: z.number().finite().nullable().optional(),
    /** SOFT reference to the measured BEDES term (center anchor); the qualifier
     * signature travels in metadata.qualifier_sig. */
    bedes_term_id: z.string().min(1).max(200).nullable().optional(),
    /** Max decimal places for numeric values (0 = whole numbers, null/absent =
     * unrestricted). Validation + presentation facet. */
    numeric_scale: z.number().int().min(0).max(6).nullable().optional(),
    applies_to_premises_levels: premisesLevelsSchema.optional(),
    requires_verification: z.boolean().optional(),
    /** R3: advisory level for PLACE-SCOPED fields only (use-scoped fields carry
     * their level on the requirements junction, per occupancy). */
    requirement_level: requirementLevelSchema.nullable().optional(),
    sort_order: z.number().int().min(-100000).max(100000).optional(),
    is_active: z.boolean().optional(),
    metadata: z.record(z.unknown()).optional(),
};
const boundsRefine = (d) => d.min_value == null || d.max_value == null || d.min_value <= d.max_value;
/** POST /orgs/:orgId/place-detail-definitions (Pattern B insert).
 * `storage`/`column_ref` are NOT client-writable — column-backed registry rows
 * are migration-seeded boundary manifest entries (phase-2.md R2). */
export const createPlaceDetailDefinitionSchema = z
    .object({
    scope: placeTaxonomyScopeSchema.default("org"),
    key: definitionKeySchema,
    category: detailCategorySchema,
    data_type: dataTypeSchema,
    is_use_scoped: z.boolean().default(false),
    ...definitionCore,
})
    .refine((d) => d.data_type !== "enum" || (d.enum_options?.length ?? 0) > 0, {
    message: "enum definitions must list enum_options",
    path: ["enum_options"],
})
    .refine(boundsRefine, {
    message: "min_value must be <= max_value",
    path: ["min_value"],
})
    .refine((d) => !(d.is_use_scoped && d.requirement_level != null), {
    message: "requirement_level lives on the requirements junction for use-scoped fields",
    path: ["requirement_level"],
});
/** PATCH /orgs/:orgId/place-detail-definitions/:definitionId — mutable facets
 * only. key / category / data_type / is_use_scoped / scope are identity:
 * changing them would strand recorded values, so they are not patchable. */
export const updatePlaceDetailDefinitionSchema = z
    .object(definitionCore)
    .partial()
    .refine((d) => Object.keys(d).length > 0, { message: "empty update" })
    .refine(boundsRefine, {
    message: "min_value must be <= max_value",
    path: ["min_value"],
});
const requirementRowSchema = z
    .object({
    definition_id: z.string().uuid(),
    /** NULL on org rows = inherit the global level. */
    requirement_level: requirementLevelSchema.nullable().optional(),
    is_hidden: z.boolean().default(false),
    /** NULL = inherit the global order. */
    sort_order: z.number().int().min(-100000).max(100000).nullable().optional(),
})
    .refine((r) => !(r.is_hidden && r.requirement_level != null), {
    message: "a hidden row carries no requirement_level",
    path: ["requirement_level"],
});
/**
 * PUT /orgs/:orgId/classifications/:value/detail-requirements → the
 * set_classification_detail_requirements RPC: atomic bulk replace of the
 * caller-scope rows for ONE occupancy classification.
 */
export const setClassificationRequirementsSchema = z
    .object({
    scope: placeTaxonomyScopeSchema.default("org"),
    rows: z.array(requirementRowSchema).max(500),
})
    .superRefine((body, ctx) => {
    const seen = new Set();
    body.rows.forEach((row, i) => {
        if (seen.has(row.definition_id)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `duplicate definition_id ${row.definition_id}`,
                path: ["rows", i, "definition_id"],
            });
        }
        seen.add(row.definition_id);
        if (body.scope === "global") {
            // Global rows ARE the default relationship: level required, no hiding.
            if (row.is_hidden) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "global rows cannot be hidden",
                    path: ["rows", i, "is_hidden"],
                });
            }
            if (row.requirement_level == null) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "global rows must carry a requirement_level",
                    path: ["rows", i, "requirement_level"],
                });
            }
        }
        else if (!row.is_hidden &&
            row.requirement_level == null &&
            row.sort_order == null) {
            // Mirrors the pcdr_org_row_meaningful CHECK — reject early with a
            // readable message instead of a constraint error.
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "an org row must customize something (level, hide, or order)",
                path: ["rows", i],
            });
        }
    });
});
//# sourceMappingURL=placeTaxonomy.js.map